import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { TextArea } from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import { useDeleteProviderAccommodation } from "../../hooks/mutations/reports.mutations";
import { IProviderAccommodation } from "../../types/reports.types";

interface DeleteProviderAccommodationFormProps {
    accommodation: IProviderAccommodation;
    onFormSubmit: () => void;
}

const schema = yup.object({
    reason: yup.string().trim().required("Reason is required"),
});

export default function DeleteProviderAccommodationForm({
    accommodation,
    onFormSubmit,
}: DeleteProviderAccommodationFormProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<{ reason: string }>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const queryClient = useQueryClient();
    const { toast } = useToast();
    const deleteAccommodation = useDeleteProviderAccommodation(
        accommodation.accommodation_id
    );

    const onSubmit = ({ reason }: { reason: string }) => {
        deleteAccommodation.mutate(reason.trim(), {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["provider-accommodations"],
                });
                toast({ mode: "success", message: "Accommodation deleted" });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete the accommodation at this time",
                });
            },
        });
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            id="delete-provider-accommodation"
            className="flex flex-col gap-[--spacing-items]"
        >
            <p className="text-base">
                Delete only accommodations entered by mistake. Notes in its
                dates will be measured against the default window. To end an
                accommodation that applied, edit it and set an end date instead.
            </p>
            <div className="fg">
                <TextArea
                    {...register("reason")}
                    label="Reason"
                    isRequired
                    hasError={!!errors.reason}
                    errorText={errors.reason?.message}
                />
            </div>
        </form>
    );
}
